import React, { useState } from "react";
import { updateJob } from "../../apis/JobApis";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";

export default function EditJobModal({ job, onClose, onSuccess }) {
  const { user } = useAuth();
  const { errorToast } = useToastHelpers();

  const [formData, setFormData] = useState({
    title: job.title || "",
    companyName: job.companyName || "",
    companyLogo: job.companyLogo || "",
    type: job.type || "job",
    experience: job.experience?.join(", ") || "",
    primarySkills: job.primarySkills?.join(", ") || "",
    secondarySkills: job.secondarySkills?.join(", ") || "",
    locations: job.locations?.join(", ") || "",
    description: job.description?.join("\n") || "",
    applyUrl: job.applyUrl || "",
    hrContact: job.hrContact || "",
    expiryDate: job.expiryDate
      ? new Date(job.expiryDate).toISOString().split("T")[0]
      : "",
    isVerified: job.isVerified || false,
    hashtags: job.hashtags?.join(", ") || "",
  });

  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        title: formData.title.trim(),
        companyName: formData.companyName.trim(),
        companyLogo: formData.companyLogo.trim(),
        type: formData.type,
        experience: formData.experience
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        primarySkills: formData.primarySkills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        secondarySkills: formData.secondarySkills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        locations: formData.locations
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        description: formData.description
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        applyUrl: formData.applyUrl.trim(),
        hrContact: formData.hrContact.trim(),
        expiryDate: formData.expiryDate
          ? new Date(formData.expiryDate).toISOString()
          : null,
        isVerified: formData.isVerified,
        hashtags: formData.hashtags
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };

      const updatedJob = await updateJob(job.id, payload, user.token);
      onSuccess(updatedJob);
    } catch (error) {
      errorToast(
        error.response?.data?.message || "Failed to update job"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Edit Job</h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white text-2xl"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-130px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="ca-input"
              />
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name *
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                className="ca-input"
              />
            </div>

            {/* Company Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Logo URL *
              </label>
              <input
                type="url"
                name="companyLogo"
                value={formData.companyLogo}
                onChange={handleChange}
                required
                className="ca-input"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                className="ca-input"
              >
                <option value="job">Job</option>
                <option value="internship">Internship</option>
                <option value="referral">Referral</option>
              </select>
            </div>

            {/* Experience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Experience (comma-separated)
              </label>
              <input
                type="text"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                placeholder="e.g., 0-2 years, Freshers"
                className="ca-input"
              />
            </div>

            {/* Primary Skills */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Skills (comma-separated) *
              </label>
              <input
                type="text"
                name="primarySkills"
                value={formData.primarySkills}
                onChange={handleChange}
                required
                placeholder="e.g., React, Node.js, Python"
                className="ca-input"
              />
            </div>

            {/* Secondary Skills */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secondary Skills (comma-separated)
              </label>
              <input
                type="text"
                name="secondarySkills"
                value={formData.secondarySkills}
                onChange={handleChange}
                placeholder="e.g., AWS, Docker"
                className="ca-input"
              />
            </div>

            {/* Locations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Locations (comma-separated) *
              </label>
              <input
                type="text"
                name="locations"
                value={formData.locations}
                onChange={handleChange}
                required
                placeholder="e.g., Bangalore, Remote"
                className="ca-input"
              />
            </div>

            {/* Apply URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apply URL *
              </label>
              <input
                type="url"
                name="applyUrl"
                value={formData.applyUrl}
                onChange={handleChange}
                required
                className="ca-input"
              />
            </div>

            {/* HR Contact */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HR Contact
              </label>
              <input
                type="text"
                name="hrContact"
                value={formData.hrContact}
                onChange={handleChange}
                className="ca-input"
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date *
              </label>
              <input
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                required
                className="ca-input"
              />
            </div>

            {/* Hashtags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hashtags (comma-separated)
              </label>
              <input
                type="text"
                name="hashtags"
                value={formData.hashtags}
                onChange={handleChange}
                placeholder="e.g., #hiring, #remote"
                className="ca-input"
              />
            </div>

            {/* Verified checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="isVerified"
                id="isVerified"
                checked={formData.isVerified}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isVerified" className="text-sm text-gray-700">
                Mark as Verified
              </label>
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (one point per line) *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={5}
                className="ca-input"
                placeholder="Enter each description point on a new line"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="ca-btn-primary px-6 py-2 inline-flex items-center gap-2"
            >
              {saving && (
                <i className="ri-loader-4-line animate-spin"></i>
              )}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
